import type { ReactNode } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/system/Box';

import TableSkeleton from '@/components/shared/Loaders/TableSkeleton';
import type { AccessQueryState } from '@/query/useAccessQuery';

import { AccessState } from './index';

/**
 * The two wrappers of a chart and of a list / table on `useAccessQuery`
 * (UI state plan, part 8b). They show the request's state the same way on
 * every page and the children only when there is data:
 *
 * - `loading`: the spinner the charts use / the table skeleton the lists use;
 * - `no_access`, `not_found`, `error`: always the shared AccessState texts
 *   (part 8a: a refusal never names what exists outside the user's scope);
 * - `no_data`: the shared AccessState text, or the page's own `empty` block
 *   (an empty list that offers "create the first one");
 * - `idle`: nothing was asked (no brand / partner chosen): `idle`, else nothing;
 * - `ok`: the children.
 */
type BlockProps = {
  state: AccessQueryState;
  /** The chart / list. A function is only called when there is data. */
  children?: ReactNode | (() => ReactNode);
  /** Replaces the default loading view. */
  loading?: ReactNode;
  /** Replaces the shared "no data" text. */
  empty?: ReactNode;
  /** Shown while nothing is asked. */
  idle?: ReactNode;
  /** Replaces the sentence of the shared no-data text. */
  noDataText?: string;
};

type ChartProps = BlockProps & {
  /** Height the chart takes, kept while loading and for the states (no layout jump). */
  height?: number | string;
};

type ListProps = BlockProps & {
  /** Rows and columns of the loading skeleton. */
  rows?: number;
  columns?: number;
};

/**
 * For a component that has its own loading and empty views (a data grid, a
 * chart that draws its empty axes): only a refusal or a failure replaces it.
 */
export const refusedState = (state: AccessQueryState): AccessQueryState =>
  state === 'no_access' || state === 'not_found' || state === 'error' ? state : 'ok';

const content = (children: BlockProps['children']): ReactNode =>
  typeof children === 'function' ? children() : (children ?? null);

function stateView(
  { state, children, loading, empty, idle, noDataText }: BlockProps,
  defaultLoading: ReactNode,
  // eslint-disable-next-line no-unused-vars
  frame: (node: ReactNode) => ReactNode,
): ReactNode {
  if (state === 'ok') return content(children);
  if (state === 'idle') return idle ?? null;
  if (state === 'loading') return loading ?? defaultLoading;
  if (state === 'no_data' && empty !== undefined) return empty;
  return frame(<AccessState state={state} text={state === 'no_data' ? noDataText : undefined} />);
}

/** A chart: spinner while loading, the shared state texts, else the chart. */
export function ChartAccessState({ height = 390, ...props }: ChartProps) {
  const centred = (node: ReactNode) => (
    <Box
      data-testid='chart-access-state'
      sx={{ alignItems: 'center', display: 'flex', height, justifyContent: 'center' }}
    >
      {node}
    </Box>
  );
  return <>{stateView(props, centred(<CircularProgress disableShrink />), centred)}</>;
}

/** A list / table: skeleton rows while loading, the shared state texts, else the list. */
export function ListAccessState({ rows = 10, columns = 6, ...props }: ListProps) {
  const framed = (node: ReactNode) => (
    <Box data-testid='list-access-state' sx={{ my: 3 }}>
      {node}
    </Box>
  );
  return <>{stateView(props, <TableSkeleton rows={rows} columns={columns} />, framed)}</>;
}
